import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import PaginationControls from "../../../../components/ACS-Dashboard/ACS-Blocks/PaginationControls";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { usePagination } from "../hooks/usePagination";
import { useDataFetching } from "../hooks/useDataFetching";
import { contractApi } from "../api/contractApi";
import { routeApi } from "../api/routeApi";
import { laneApi } from "../api/laneApi";
import { safe } from "../utils/utils";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/contractsSlice";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";
import { buildFilterNode, countActiveFilters } from "../../../AdvancedFilter/filterTree";
import { AdvancedFilterToolbar } from "../../../AdvancedFilter/AdvancedFilterToolbar";
import { AdvancedFilterModal } from "../../../AdvancedFilter/AdvancedFilterModal";
import { SavedFiltersControl } from "../../../AdvancedFilter/SavedFiltersControl";
import { ROUTE_RULE_FIELDS } from "../routesFilterFields";

import {
  formatAsLocalDate,
  convertUTCToLocalTime, // eslint-disable-line no-unused-vars
  convertDateToUTC,     // eslint-disable-line no-unused-vars
  convertTimeToUTC,     // eslint-disable-line no-unused-vars
} from "../utils/formatters";

const ROUTE_HEADERS = [
  "Selection",
  "Action",
  "Contract Code",
  "Lane Code",
  "Route Code",
  "Office Code",
  "Origin",
  "Destination",
  "Mode",
  "Capacity",
  "Commitment",
  "Carrier",
  "Shipper",
  "Carrier Code",
  "Shipper Code",
  "Agent Code",
  "Agent Name",
  "Customer Nominated",
  "Equipment",
  "Effective Start",
  "Effective End",
  "Distance",
  "Unit",
  "Assoc.Rates",
  "Auto Assign Carrier",
  "Auto Tender Shipment",
  "Auto Assign Carrier on Recall",
  "Carrier Response Time (in mins)",
];

const SORTABLE_COLUMNS = {
  "Contract Code":                    "lane.contract.contractCode",
  "Lane Code":                        "lane.laneCode",
  "Route Code":                       "routeCode",
  "Office Code":                      "officeCode",
  "Origin":                           "origin",
  "Destination":                      "destination",
  "Mode":                             "mode",
  "Capacity":                         "capacity",
  "Commitment":                       "commitment",
  "Carrier":                          "carrier",
  "Shipper":                          "shipper",
  "Carrier Code":                     "carrierCode",
  "Shipper Code":                     "shipperCode",
  "Agent Code":                       "agentCode",
  "Agent Name":                       "agentName",
  "Customer Nominated":               "customerNominatedCarrier",
  "Equipment":                        "equipment",
  "Effective Start":                  "startDate",
  "Effective End":                    "endDate",
  "Distance":                         "distance",
  "Unit":                             "unit",
  "Assoc.Rates":                      "assocRates",   // in-memory sort
  "Auto Assign Carrier":              "autoAssign",
  "Auto Tender Shipment":             "autoTender",
  "Auto Assign Carrier on Recall":    "autoAssignOnRecall",
  "Carrier Response Time (in mins)":  "responseTime",
};

const formatUnit = (unit, formatter) => (!unit ? "" : formatter(unit));

const formatCapacityUnit   = (unit) => formatUnit(unit, (u) => u.split("_").map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(" "));
const formatCommitmentUnit = (unit) => formatUnit(unit, (u) => u.split("_").map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(" "));
const formatDistanceUnit   = (unit) => {
  switch (unit?.toUpperCase()) {
    case "KILOMETERS": return "km";
    case "MILES":      return "mi";
    default:           return unit?.toLowerCase() || "-";
  }
};

const RoutesDetails = ({ officeCode, searchTerm = null, fetchFn = null }) => {
  const { pagination, setPagination, handlePageChange, handlePerPageChange } = usePagination();

  const [sortBy,     setSortBy]     = useState("updatedAt");
  const [ascending,  setAscending]  = useState(false);
  const [routes,         setRoutes]         = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [isLoading,      setIsLoading]      = useState(true);
  const [openMenu,       setOpenMenu]       = useState(null);
  const [dropdownPos,    setDropdownPos]    = useState({ top: 0, left: 0 });
  const [columnWidths, setColumnWidths] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState({ rules: [], matchType: "AND" });
  const [showFilterModal, setShowFilterModal] = useState(false);


  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const dispatch          = useDispatch();
  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.CONTRACT_ROUTES,
    allHeaders: ROUTE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Contract Code"],
  });
  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const formattedRoutes = useMemo(
    () =>
      routes.map((route) => ({
        ...route,
        id:               safe(() => route.id, "number"),
        contractId:       safe(() => route.contractId, "number"),
        "Contract Code":  safe(() => route.contractCode),
        "Lane Code":      safe(() => route.laneCode),
        "Route Code":     safe(() => route.routeCode),
        "Office Code":    safe(() => route.officeCode),
        Origin:           safe(() => route.origin),
        Destination:      safe(() => route.destination),
        Mode:             safe(() => route.mode),
        Capacity:         safe(() => route.capacity ? `${route.capacity} ${formatCapacityUnit(route.capacityUnit)}` : "-"),
        Commitment:       safe(() => route.commitment ? `${route.commitment} ${formatCommitmentUnit(route.commitmentUnit)}` : "-"),
        Carrier:          safe(() => route.carrier),
        Shipper:          safe(() => route.shipper),
        "Carrier Code":   safe(() => route.carrierCode),
        "Shipper Code":   safe(() => route.shipperCode),
        "Agent Code":     safe(() => route.agentCode),
        "Agent Name":     safe(() => route.agentName),
        "Customer Nominated": safe(() => (route.customerNominatedCarrier ? "✓" : "✗")),
        Equipment:        safe(() => route.equipment),
        "Effective Start": safe(() => formatAsLocalDate(route.startDate), "string", "-"),
        "Effective End":   safe(() => formatAsLocalDate(route.endDate),   "string", "-"),
        Distance:         safe(() => route.distance, "number"),
        Unit:             safe(() => formatDistanceUnit(route.unit)),
        "Assoc.Rates":    safe(() => route.rateIds?.length, "number", 0),
        "Auto Assign Carrier":          safe(() => (route.autoAssign        ? "✓" : "✗")),
        "Auto Tender Shipment":         safe(() => (route.autoTender        ? "✓" : "✗")),
        "Auto Assign Carrier on Recall":safe(() => (route.autoAssignOnRecall? "✓" : "✗")),
        "Carrier Response Time (in mins)": safe(() => route.responseTime),
      })),
    [routes]
  );

  const handleRowDoubleClick = useCallback(async (rowId) => {
    const selectedRouteData = formattedRoutes.find((r) => r.id === rowId);
    if (!selectedRouteData) return;

    try {
      const routeOfficeCode = selectedRouteData.officeCode || officeCode;
      let contractIdToUse   = selectedRouteData.contractId;

      if (contractIdToUse === "-") {
        const laneId = selectedRouteData.laneId;
        if (!laneId) { console.error("No lane ID found:", selectedRouteData); return; }
        try {
          const laneRes  = await laneApi.getLaneById(routeOfficeCode, laneId);
          contractIdToUse = (laneRes.data || laneRes).contractId;
        } catch (e) { console.error("Failed to fetch lane:", e); return; }
      }

      if (!contractIdToUse) { console.error("No contract ID found:", selectedRouteData); return; }

      let contractData;
      try {
        const res  = await contractApi.getContractById(routeOfficeCode, contractIdToUse);
        contractData = res.data || res;
      } catch {
        contractData = {
          contractId:    contractIdToUse,
          contractCode:  selectedRouteData["Contract Code"],
          officeCode:    routeOfficeCode,
          status:        "ACTIVE",
          effectiveDate: null, expirationDate: null,
        };
      }

      dispatch(addTab({
        tabData: {
          name:            `Contract: ${contractData.contractCode || selectedRouteData["Contract Code"]}`,
          type:            "contractDetails",
          contractId:      contractIdToUse,
          officeCode:      routeOfficeCode,
          contractData,
          expandedDataKey: `route-${rowId}`,
        },
      }));
    } catch (error) {
      console.error("Error opening contract details:", error);
    }
  }, [formattedRoutes, officeCode, dispatch]);

  const handleSort = (newSortBy) => {
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
  };

  const onHeaderSortClick = (headerLabel) => {
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    handleSort(key);
  };

  // Memoized: buildFilterNode returns a new object every call; an unmemoized recompute
  // here changes fetchRoutes' identity every render, retriggering its effect in a loop.
  const filterNode = useMemo(
    () => buildFilterNode(ROUTE_RULE_FIELDS, advancedFilters),
    [advancedFilters]
  );

  const fetchRoutes = useCallback(
    (params) => {
      const apiParams = {
        page: pagination.currentPage - 1,
        size: pagination.perPage,
        sortBy,
        ascending,
        ...params,
      };
      if (searchTerm && fetchFn) {
        return fetchFn({ officeCode, query: searchTerm, status: "", filter: filterNode }, apiParams);
      }
      return routeApi.searchRoutes({ filter: filterNode }, apiParams);
    },
    [searchTerm, fetchFn, officeCode, pagination.currentPage, pagination.perPage, sortBy, ascending, filterNode]
  );

  useDataFetching({
    fetchFn: fetchRoutes,
    officeCode,
    onSuccess: useCallback((data) => {
      setRoutes(data.content);
      setPagination((prev) => ({ ...prev, total: data.totalElements }));
      setIsLoading(false);
    }, [setPagination]),
    onError: () => setIsLoading(false),
  });

  const handleSelectAll      = useCallback((checked) => setSelectedRoutes(checked ? formattedRoutes.map((r) => r.id) : []), [formattedRoutes]);
  const handleSingleCheckbox = useCallback((id) => setSelectedRoutes((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]), []);

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Routes..." />
      ) : (
        <>
          {!searchTerm && (
            <AdvancedFilterToolbar
              ruleFields={ROUTE_RULE_FIELDS}
              filters={advancedFilters}
              activeCount={countActiveFilters(ROUTE_RULE_FIELDS, advancedFilters)}
              onOpenModal={() => setShowFilterModal(true)}
              onClearAll={() => setAdvancedFilters({ rules: [], matchType: "AND" })}
              onChangeFilters={setAdvancedFilters}
              trailing={
                <div className="flex items-center gap-2">
                  <SavedFiltersControl
                    pageKey={TABLE_PAGE_KEYS.CONTRACT_ROUTES}
                    ruleFields={ROUTE_RULE_FIELDS}
                    currentFilters={advancedFilters}
                    onApply={setAdvancedFilters}
                  />
                  <TableSavedViewsControl
                    controlLabel={controlLabel}
                    views={views}
                    activeView={activeView}
                    loading={viewsLoading}
                    error={viewsError}
                    isDirty={isDirty}
                    allDataHeaders={ROUTE_HEADERS.slice(2)}
                    dataHeaders={dataHeaders}
                    onSelectView={selectView}
                    onSaveNew={saveNewView}
                    onUpdateActive={updateActiveView}
                    onRename={renameView}
                    onDelete={deleteView}
                    onSetDefault={setDefaultView}
                    onUpdateColumnVisibility={updateColumnVisibility}
                    onManageColumns={onManageColumns}
                  />
                </div>
              }
            />
          )}

          {searchTerm && (
            <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={ROUTE_HEADERS.slice(2)}
                dataHeaders={dataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
            </div>
          )}
{showFilterModal && (
            <AdvancedFilterModal
              ruleFields={ROUTE_RULE_FIELDS}
              filters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setShowFilterModal(false)}
            />
          )}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              data={formattedRoutes}
              initialColumnWidths={columnWidths}
              onColumnResize={setColumnWidths}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              selectedRows={selectedRoutes}
              totalValidRows={formattedRoutes.length}
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => {}}
              checkboxHeaderRef={checkboxHeaderRef}
              sortBy={sortBy}
              ascending={ascending}
              onSort={onHeaderSortClick}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
                const id     = row.id;
                const isOpen = openMenu === id;
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
                      style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                    >
                      <input
                        type="checkbox"
                        className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        checked={selectedRoutes.includes(id)}
                        onChange={() => handleSingleCheckbox(id)}
                      />
                    </td>

                    {/* Column 1 — Action (portal dropdown) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
                      style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen && ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                      >
                        <ActionMenu
                          rowId={id}
                          isOpen
                          moduleName="Route"
                          onClose={() => setOpenMenu(null)}
                          actions={[
                            { icon: "Eye", label: "View", color: "blue", action: () => handleRowDoubleClick(id) },
                          ]}
                        />
                      </div>,
                      document.body
                    )}

                    {/* Columns 2+ — Data */}
                    {dataHeadersToRender.map((col) => {
                      return (
                        <td
                          key={col}
                          className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(col).className}`}
                          style={{
                            ...getStickyCellProps(col).style,
                            whiteSpace:   "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            width:        "200px",
                            minWidth:     "200px",
                            maxWidth:     "200px",
                          }}
                          onDoubleClick={() => handleRowDoubleClick(id)}
                        >
                          {row[col]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>
          </div>

          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            moduleName="Routes"
            className="mt-4 p-2 border-t border-gray-200 dark:border-acs-dark-border"
          />
        </>
      )}
    </div>
  );
};

export default RoutesDetails;
